import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, Plus, X, ShoppingBag, Gift, Percent } from 'lucide-react';
import HookItUpButton from '../components/HookItUpButton';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import api from '../services/api';

const ORIGINAL_HERO = 'https://customer-assets.emergentagent.com/job_prsvr-checkout-debug/artifacts/i3otimp3_IMG_6890.jpeg';

// Model images - ACTUAL model shots, not product images
const MODEL_IMAGES = {
  redBlack: 'https://customer-assets.emergentagent.com/job_0309c6a4-a7bd-45fc-9a0d-971b04896498/artifacts/58xa38jm_IMG_6884.jpeg',
  blackRed: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/97E9508E-226A-46B3-B5CE-CAD7B5809F12.jpg?v=1763931516',
  blackWhite: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/DAA166DA-8124-4523-B6C3-A98B1D22DFCD.jpg?v=1763675517',
  redBlackBlue: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/CB447EAD-D5A7-4DEE-A159-6FE81BF9C8E3.jpg?v=1763511605',
  motorcycle: 'https://customer-assets.emergentagent.com/job_0309c6a4-a7bd-45fc-9a0d-971b04896498/artifacts/lolvin3v_IMG_6896.jpeg',
  sneakers: 'https://customer-assets.emergentagent.com/job_prsvr-checkout-debug/artifacts/qhaojpe6_IMG_6893.jpeg',
};

const SHOPIFY_BASE = 'https://prsvr.store/products/';

const PRODUCTS = {
  suits: [
    { id: 'suit-red-black', name: 'Valley Track Suit', variant: 'Red/Black', price: 1000,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/Red_and_Black_Valley_Set.png?v=1764097510',
      modelImage: MODEL_IMAGES.redBlack, handle: 'valley-track-suit-red-black', colorSwatch: '#DC2626' },
    { id: 'suit-black-red', name: 'Valley Track Suit', variant: 'Black/Red', price: 1000,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/Black_and_Red_Valley_Set.png?v=1763931516',
      modelImage: MODEL_IMAGES.blackRed, handle: 'valley-track-suit-black-red', colorSwatch: '#1F1F1F' },
    { id: 'suit-black-white', name: 'Valley Track Suit', variant: 'Black/White', price: 1000,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/Black_and_White_Valley_Set.png?v=1763932119',
      modelImage: MODEL_IMAGES.blackWhite, handle: 'valley-track-suit-black-white', colorSwatch: '#FFFFFF' },
    { id: 'suit-red-black-blue', name: 'Valley Track Suit', variant: 'Red/Black/Blue', price: 1000,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/CB447EAD-D5A7-4DEE-A159-6FE81BF9C8E3.jpg?v=1763511605',
      modelImage: MODEL_IMAGES.redBlackBlue, handle: 'valley-bomber-red-white-blue', colorSwatch: '#1E40AF' }
  ],
  jackets: [
    { id: 'jacket-red-black', name: 'The Valley Bomber', variant: 'Red/Black', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/IMG_6201.jpg?v=1763437393',
      handle: 'valley-bomber-red-black', colorSwatch: '#DC2626' },
    { id: 'jacket-black-red', name: 'The Valley Bomber', variant: 'Black/Red', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/Black_and_Red_Valley_Bomber.jpg?v=1763931516',
      handle: 'the-valley-bomber', colorSwatch: '#1F1F1F' },
    { id: 'jacket-black-white', name: 'The Valley Bomber', variant: 'Black/White', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/DAA166DA-8124-4523-B6C3-A98B1D22DFCD.jpg?v=1763675517',
      handle: 'the-valley-bomber-black-white', colorSwatch: '#FFFFFF' },
    { id: 'jacket-red-white-blue', name: 'The Valley Bomber', variant: 'Red/Black/Blue', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/1_600ac833-f3df-4fbe-a295-cb04c43a25f0.jpg?v=1762069599',
      handle: 'valley-bomber-red-white-blue', colorSwatch: '#1E40AF' }
  ],
  pants: [
    { id: 'pants-red-black', name: 'Leather Valley Pants', variant: 'Red/Black', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/red_valley_leather_pants.jpg?v=1763667685',
      handle: 'chi-valley-track-pants', colorSwatch: '#DC2626' },
    { id: 'pants-black-red', name: 'Leather Valley Pants', variant: 'Black/Red', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/Red_and_Black_Valley_Track_Pants.jpg?v=1763931516',
      handle: 'valley-track-suit-black-red', colorSwatch: '#1F1F1F' },
    { id: 'pants-black-white', name: 'Leather Valley Pants', variant: 'Black/White', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/IMG_7597.jpg?v=1762069568',
      handle: 'valley-track-pants-black-white', colorSwatch: '#FFFFFF' },
    { id: 'pants-red-black-blue', name: 'Leather Valley Pants', variant: 'Black/Red/Blue', price: 544,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/10F4D503-B8EB-492A-9571-82DA29AF4DDA.jpg?v=1763511111',
      handle: 'valleyleathertrackpants', colorSwatch: '#1E40AF' }
  ],
  footwear: [
    { id: 'valley-high', name: 'Valley High Sneakers', variant: 'Black/White', price: 188,
      image: 'https://cdn.shopify.com/s/files/1/0733/6116/2292/files/A0C8D3AB-3AF0-4D45-A538-4E1764C81148.jpg?v=1763667597',
      handle: 'the-valley-high-top-sneaker' }
  ]
};

const SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
const SHOE_SIZES = ['7', '8', '9', '10', '11', '12', '13'];
const SET_SAVINGS = 88;

const ValleyLeatherShop = () => {
  const navigate = useNavigate();
  const { addToCart: addToGlobalCart, cart: globalCart } = useCart();
  const [currentSuitIndex, setCurrentSuitIndex] = useState(0);
  const [shopMode, setShopMode] = useState('set');
  const [selectedJacketIdx, setSelectedJacketIdx] = useState(0);
  const [selectedPantsIdx, setSelectedPantsIdx] = useState(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [sizeSelections, setSizeSelections] = useState({});
  const [addingToCart, setAddingToCart] = useState(null);
  const [productVariants, setProductVariants] = useState({}); // Store real Shopify variants
  const touchStartX = useRef(0);

  // Fetch real variant IDs from Shopify on mount
  useEffect(() => {
    const fetchVariants = async () => {
      const handles = [
        ...PRODUCTS.suits.map(p => p.handle),
        ...PRODUCTS.jackets.map(p => p.handle),
        ...PRODUCTS.pants.map(p => p.handle),
        ...PRODUCTS.footwear.map(p => p.handle)
      ];
      const uniqueHandles = [...new Set(handles)];
      
      const variantsMap = {};
      for (const handle of uniqueHandles) {
        try {
          const res = await api.get(`/shopify/products/${handle}`);
          if (res.data.success && res.data.product?.variants) {
            variantsMap[handle] = res.data.product.variants;
          }
        } catch (e) {
          console.log(`Could not fetch variants for ${handle}`);
        }
      }
      setProductVariants(variantsMap);
    };
    fetchVariants();
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };

  const handleSizeSelect = (id, size) => {
    setSizeSelections(prev => ({ ...prev, [id]: size }));
  };

  const handleAddToCart = async (product, sizeKey) => {
    const size = sizeSelections[sizeKey];
    if (!size) {
      toast.error('Please select a size first');
      return;
    }
    
    // Find the real Shopify variant ID
    const variants = productVariants[product.handle];
    if (!variants || variants.length === 0) {
      toast.error('Product not available. Please try again.');
      return;
    }
    
    // Find variant matching selected size
    const variant = variants.find(v => v.title?.toUpperCase() === size.toUpperCase());
    if (!variant) {
      toast.error(`Size ${size} not available`);
      return;
    }
    
    setAddingToCart(sizeKey);
    try {
      await addToGlobalCart(variant.id, 1, `${product.name} (${product.variant}) - ${size}`);
      setSizeSelections(prev => ({ ...prev, [sizeKey]: null }));
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const goToProduct = (handle) => window.open(SHOPIFY_BASE + handle, '_blank');

  const getCartCount = () => {
    if (!globalCart || !globalCart.lineItems) return 0;
    return globalCart.lineItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <div className="bg-neutral-900 text-white min-h-screen overflow-x-hidden">
      {showStickyBar && getCartCount() > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur border-t border-neutral-700 py-3 px-4">
          <div className="container mx-auto flex items-center justify-between max-w-full">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-red-500" />
              <span className="text-sm">{getCartCount()} items in cart</span>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => window.location.href = globalCart?.webUrl || '/cart'} className="bg-red-600 hover:bg-red-500 text-white text-sm px-4 py-2">
                View Cart
              </Button>
            </div>
          </div>
        </div>
      )}

      <section className="relative h-screen w-full overflow-hidden">
        <img src={ORIGINAL_HERO} alt="Valley Collection" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
        <div className="absolute bottom-20 left-4 right-4 md:left-16 md:right-16">
          <p className="text-neutral-400 uppercase tracking-widest text-xs md:text-sm mb-2">The Valley Collection</p>
          <h1 className="text-3xl md:text-6xl font-black uppercase mb-4 text-white leading-tight">Premium Leather<br/>Track Suits</h1>
          <p className="text-neutral-300 text-sm md:text-lg mb-6 max-w-md">Bold. Unapologetic. Unmistakable.</p>
          <Button onClick={() => document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 md:px-8 md:py-4">
            Shop The Collection <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <section id="shop" className="py-12 md:py-16 bg-neutral-950 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col items-center mb-8">
            <p className="text-neutral-400 uppercase tracking-widest text-xs mb-3">Ways to Shop</p>
            <div className="flex gap-1 bg-neutral-900 p-1 rounded-lg overflow-x-auto max-w-full">
              {['set', 'separates'].map(mode => (
                <button key={mode} type="button" onClick={() => setShopMode(mode)}
                  className={`px-4 py-2 md:px-6 md:py-3 rounded-md text-xs md:text-sm font-bold uppercase whitespace-nowrap ${shopMode === mode ? 'bg-red-600 text-white' : 'text-neutral-400'}`}>
                  {mode === 'set' ? 'Complete Set' : 'Separates'}
                </button>
              ))}
            </div>
          </div>

          {/* Savings Banner - Shows for ALL modes */}
          <div className="text-center p-3 bg-green-900/30 border border-green-700/50 rounded-xl mb-8">
            <p className="text-green-400 font-bold uppercase tracking-wider text-sm">
              <Gift className="w-4 h-4 inline mr-2" /> The More You Add, The More You Save!
            </p>
            <p className="text-neutral-400 text-xs mt-1">Complete Set: Save ${SET_SAVINGS} • 20% off each additional jacket or pants • No code needed</p>
          </div>

          {shopMode === 'set' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-neutral-900 rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-neutral-400 uppercase tracking-wider text-xs">Complete Set • Save ${SET_SAVINGS}</p>
                  <span className="text-xl md:text-2xl font-black text-white">${PRODUCTS.suits[currentSuitIndex].price}</span>
                </div>
                <div className="relative aspect-[4/5] mb-4 bg-neutral-800 rounded-lg overflow-hidden"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={(e) => {
                    const diff = touchStartX.current - e.changedTouches[0].clientX;
                    if (Math.abs(diff) > 50) {
                      if (diff > 0) setCurrentSuitIndex(i => (i + 1) % PRODUCTS.suits.length);
                      else setCurrentSuitIndex(i => (i - 1 + PRODUCTS.suits.length) % PRODUCTS.suits.length);
                    }
                  }}
                  onClick={() => goToProduct(PRODUCTS.suits[currentSuitIndex].handle)}>
                  <img src={PRODUCTS.suits[currentSuitIndex].image} alt={PRODUCTS.suits[currentSuitIndex].variant} className="w-full h-full object-contain cursor-pointer" />
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {PRODUCTS.suits.map((_, idx) => (
                      <button key={idx} type="button" onClick={(e) => { e.stopPropagation(); setCurrentSuitIndex(idx); }}
                        className={`w-2 h-2 rounded-full ${idx === currentSuitIndex ? 'bg-red-500 w-5' : 'bg-white/50'}`} />
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-neutral-400 text-xs uppercase mb-2">Colorway: {PRODUCTS.suits[currentSuitIndex].variant}</p>
                  <div className="flex gap-2 flex-wrap">
                    {PRODUCTS.suits.map((suit, idx) => (
                      <button key={suit.id} type="button" onClick={() => setCurrentSuitIndex(idx)}
                        className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 ${idx === currentSuitIndex ? 'border-red-500' : 'border-neutral-700'}`}
                        style={{ background: suit.colorSwatch }} title={suit.variant} />
                    ))}
                  </div>
                </div>
                <SizeSelector label="Jacket Size" sizes={SIZES} selectedSize={sizeSelections['set-jacket']} 
                  onSelect={(s) => handleSizeSelect('set-jacket', s)} />
                <SizeSelector label="Pants Size" sizes={SIZES} selectedSize={sizeSelections['set-pants']} 
                  onSelect={(s) => handleSizeSelect('set-pants', s)} />
                {sizeSelections['set-jacket'] && sizeSelections['set-pants'] && (
                  <Button 
                    onClick={async () => {
                      const suit = PRODUCTS.suits[currentSuitIndex];
                      const variants = productVariants[suit.handle];
                      if (!variants) {
                        toast.error('Product not available');
                        return;
                      }
                      // For sets, find variant that matches jacket size (sets typically have one size variant)
                      const jacketSize = sizeSelections['set-jacket'];
                      const variant = variants.find(v => v.title?.toUpperCase().includes(jacketSize.toUpperCase())) || variants[0];
                      if (variant) {
                        setAddingToCart('set');
                        await addToGlobalCart(variant.id, 1, `${suit.name} (${suit.variant}) - Jacket: ${jacketSize}, Pants: ${sizeSelections['set-pants']}`);
                        setSizeSelections(prev => ({ ...prev, 'set-jacket': null, 'set-pants': null }));
                        setAddingToCart(null);
                      }
                    }}
                    disabled={addingToCart === 'set'}
                    className="w-full bg-red-600 hover:bg-red-500 text-white py-3 mt-4 font-bold uppercase text-sm disabled:opacity-50">
                    {addingToCart === 'set' ? 'Adding...' : <><Plus className="w-4 h-4 mr-2" /> Add Complete Set</>}
                  </Button>
                )}
              </div>
              <div className="relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer" onClick={() => goToProduct(PRODUCTS.suits[currentSuitIndex].handle)}>
                <img src={PRODUCTS.suits[currentSuitIndex].modelImage} alt={PRODUCTS.suits[currentSuitIndex].variant} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h2 className="text-2xl md:text-3xl font-black uppercase text-white">{PRODUCTS.suits[currentSuitIndex].variant}</h2>
                  <p className="text-neutral-300 text-sm">Valley Leather Track Suit</p>
                </div>
              </div>
            </div>
          )}

          {shopMode === 'separates' && (
            <div className="space-y-10">
              <div>
                <h3 className="text-xl md:text-2xl font-black uppercase mb-4 text-center text-white">Jackets - $544</h3>
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4">
                  {PRODUCTS.jackets.map((jacket) => (
                    <div key={jacket.id} className="flex-shrink-0 w-64 md:w-72 bg-neutral-900 rounded-xl p-4 snap-start">
                      <div className="aspect-[4/5] mb-3 rounded-lg overflow-hidden bg-neutral-800 cursor-pointer" onClick={() => goToProduct(jacket.handle)}>
                        <img src={jacket.image} alt={jacket.variant} className="w-full h-full object-cover" />
                      </div>
                      <h4 className="font-bold text-white text-sm cursor-pointer hover:text-red-500" onClick={() => goToProduct(jacket.handle)}>
                        {jacket.name} ({jacket.variant})
                      </h4>
                      <p className="text-neutral-500 text-sm mb-2">${jacket.price}</p>
                      <SizeSelector label="Size" sizes={SIZES} small selectedSize={sizeSelections[jacket.id]} 
                        onSelect={(s) => handleSizeSelect(jacket.id, s)} />
                      {sizeSelections[jacket.id] && (
                        <button 
                          onClick={() => handleAddToCart(jacket, jacket.id)}
                          disabled={addingToCart === jacket.id}
                          className="w-full mt-2 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-lg disabled:opacity-50">
                          {addingToCart === jacket.id ? 'Adding...' : 'Add to Cart'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-black uppercase mb-4 text-center text-white">Pants - $544</h3>
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4">
                  {PRODUCTS.pants.map((pant) => (
                    <div key={pant.id} className="flex-shrink-0 w-64 md:w-72 bg-neutral-900 rounded-xl p-4 snap-start">
                      <div className="aspect-[4/5] mb-3 rounded-lg overflow-hidden bg-neutral-800 cursor-pointer" onClick={() => goToProduct(pant.handle)}>
                        <img src={pant.image} alt={pant.variant} className="w-full h-full object-cover" />
                      </div>
                      <h4 className="font-bold text-white text-sm cursor-pointer hover:text-red-500" onClick={() => goToProduct(pant.handle)}>
                        {pant.name} ({pant.variant})
                      </h4>
                      <p className="text-neutral-500 text-sm mb-2">${pant.price}</p>
                      <SizeSelector label="Size" sizes={SIZES} small selectedSize={sizeSelections[pant.id]} 
                        onSelect={(s) => handleSizeSelect(pant.id, s)} />
                      {sizeSelections[pant.id] && (
                        <button 
                          onClick={() => handleAddToCart(pant, pant.id)}
                          disabled={addingToCart === pant.id}
                          className="w-full mt-2 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-lg disabled:opacity-50">
                          {addingToCart === pant.id ? 'Adding...' : 'Add to Cart'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-neutral-900 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-8">
            <p className="text-neutral-400 uppercase tracking-widest text-xs mb-2">Shop by Colorway</p>
            <h2 className="text-2xl md:text-3xl font-black uppercase text-white">The Valley Collection</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {PRODUCTS.suits.map((suit, idx) => (
              <div key={suit.id} className="relative aspect-[4/5] rounded-xl overflow-hidden group cursor-pointer"
                onClick={() => { setCurrentSuitIndex(idx); setShopMode('set'); document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' }); }}>
                <img src={suit.modelImage} alt={suit.variant} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 rounded-full border border-white/30" style={{ background: suit.colorSwatch }} />
                    <h3 className="font-bold text-xs md:text-sm text-white">{suit.variant}</h3>
                  </div>
                  <p className="font-bold text-white">${suit.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-neutral-950">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="relative aspect-video md:aspect-[21/9] rounded-xl overflow-hidden">
            <img src={MODEL_IMAGES.motorcycle} alt="Valley Lifestyle" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-neutral-950 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-2xl md:text-4xl font-light italic text-neutral-300 leading-relaxed">
            "In the Valley, we don't just wear luxury — we embody it."
          </p>
          <p className="text-red-500 uppercase tracking-widest text-sm mt-4">— PRSVR</p>
        </div>
      </section>

      <section className="py-12 bg-neutral-900 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="aspect-[4/5] rounded-xl overflow-hidden cursor-pointer" onClick={() => goToProduct('the-valley-high-top-sneaker')}>
              <img src={MODEL_IMAGES.sneakers} alt="Valley High" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-neutral-400 uppercase tracking-widest text-xs mb-2">Complete The Look</p>
              <h2 className="text-2xl md:text-3xl font-black uppercase mb-2 text-white">The Valley High</h2>
              <p className="text-neutral-400 text-sm mb-4">Premium leather sneaker to complete your Valley look.</p>
              <p className="text-xl font-bold text-white mb-4">${PRODUCTS.footwear[0].price}</p>
              <SizeSelector label="Shoe Size" sizes={SHOE_SIZES} selectedSize={sizeSelections['shoe']} 
                onSelect={(s) => handleSizeSelect('shoe', s)} />
              {sizeSelections['shoe'] && (
                <button 
                  onClick={() => handleAddToCart(PRODUCTS.footwear[0], 'shoe')}
                  disabled={addingToCart === 'shoe'}
                  className="w-full mt-3 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg disabled:opacity-50">
                  {addingToCart === 'shoe' ? 'Adding...' : 'Add to Cart'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-neutral-950 px-4 pb-32">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-6">
            <p className="text-neutral-400 uppercase tracking-widest text-xs mb-2">Your Cart</p>
            <h2 className="text-2xl md:text-3xl font-black uppercase text-white">Ready to Checkout?</h2>
          </div>
          <div className="mb-4 p-3 bg-green-900/30 border border-green-700/50 rounded-xl">
            <p className="text-green-400 text-sm">
              <Percent className="w-4 h-4 inline mr-1" /> Complete Set: Save ${SET_SAVINGS} • 2nd+ Jacket/Pants: 20% off
            </p>
          </div>
          {getCartCount() > 0 ? (
            <div className="text-center py-6">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-red-500" />
              <p className="text-lg text-white mb-2">{getCartCount()} item{getCartCount() > 1 ? 's' : ''} in your cart</p>
              <p className="text-neutral-400 text-sm mb-6">Your items have been added to your cart and will persist across pages.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => window.location.href = globalCart?.webUrl || '/cart'} 
                  className="px-8 py-3 font-bold uppercase text-sm bg-red-600 hover:bg-red-500 text-white">
                  View Cart & Checkout <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="border-neutral-600 text-white hover:bg-neutral-800 px-8 py-3 font-bold uppercase text-sm">
                  Continue Shopping
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-neutral-600">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select items above to build your Valley Look</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const SizeSelector = ({ label, sizes, selectedSize, onSelect, small }) => (
  <div className={small ? 'mt-2' : 'mb-3'}>
    <p className="text-neutral-400 text-xs uppercase mb-2">{label}</p>
    <div className="flex flex-wrap gap-1">
      {sizes.map(size => (
        <button key={size} type="button" onClick={() => onSelect(size)}
          className={`${small ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'} rounded font-bold transition-colors ${selectedSize === size ? 'bg-red-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'}`}>
          {size}
        </button>
      ))}
    </div>
  </div>
);

const CartItem = ({ item, label, sub, onRemove, onView, discount = 0 }) => (
  <div className="flex items-center justify-between p-3 bg-neutral-900 rounded-lg gap-3">
    <div className="flex items-center gap-3 cursor-pointer min-w-0 flex-1" onClick={onView}>
      <img src={item.image} alt={label} className="w-14 h-14 object-cover rounded flex-shrink-0" />
      <div className="min-w-0">
        <p className="font-bold text-white text-sm truncate hover:text-red-500">{label}</p>
        <p className="text-neutral-500 text-xs">{sub}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {discount > 0 ? (
        <div className="text-right">
          <span className="text-neutral-500 line-through text-xs">${item.price}</span>
          <span className="font-bold text-green-400 text-sm ml-1">${item.price - discount}</span>
        </div>
      ) : (
        <span className="font-bold text-white text-sm">${item.price}</span>
      )}
      <button type="button" onClick={onRemove} className="text-neutral-500 hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
    </div>
  </div>
);

export default ValleyLeatherShop;
